import { EngineResult } from './constants'
import { decision } from './decision'

// ============================================================
// MONTHLY RECURRENCE EVALUATOR
// ============================================================
//
// Supports:
//
// 1. Specific day of month
//      Example: 15th of every month
//
// 2. Calendar-anchored day of month
//      Example: 31st
//
//      Jan 31
//      Feb 28
//      Mar 31
//      Apr 30
//      May 31
//
// 3. Nth weekday of month
//      Example: 2nd Tuesday
//
// 4. Multiple nth weekdays
//      Example: 2nd and 4th Wednesday
//
// 5. Last weekday
//      Example: last Wednesday
//
// interval_value represents the number of months between
// recurrence months.
//
// ============================================================


// ============================================================
// LOCAL CALENDAR DATE
// ============================================================

function toLocalCalendarDate(value) {

    if (!value) {
        return null
    }

    if (value instanceof Date) {

        return new Date(
            value.getUTCFullYear(),
            value.getUTCMonth(),
            value.getUTCDate()
        )
    }

    if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(value)
    ) {

        const [
            year,
            month,
            day
        ] =
            value.split('-').map(Number)

        return new Date(
            year,
            month - 1,
            day
        )
    }

    const parsed =
        new Date(value)

    if (
        Number.isNaN(
            parsed.getTime()
        )
    ) {
        return null
    }

    return new Date(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate()
    )
}


// ============================================================
// DAYS IN MONTH
// ============================================================

function getDaysInMonth(
    year,
    month
) {

    return new Date(
        year,
        month + 1,
        0
    ).getDate()
}


// ============================================================
// MONTHS BETWEEN CALENDAR MONTHS
// ============================================================

function monthsBetween(
    startDate,
    currentDate
) {

    return (
        (
            currentDate.getFullYear() -
            startDate.getFullYear()
        ) * 12
        +
        (
            currentDate.getMonth() -
            startDate.getMonth()
        )
    )
}


// ============================================================
// NTH WEEKDAY MATCH
// ============================================================

function getWeekdayOccurrence(
    date,
    requestedWeekday
) {

    if (
        date.getDay() !==
        requestedWeekday
    ) {
        return null
    }

    return Math.floor(
        (
            date.getDate() - 1
        ) / 7
    ) + 1
}


// ============================================================
// IS LAST WEEKDAY?
// ============================================================

function isLastWeekday(
    date,
    requestedWeekday
) {

    if (
        date.getDay() !==
        requestedWeekday
    ) {
        return false
    }

    const nextSameWeekday =
        new Date(date)

    nextSameWeekday.setDate(
        nextSameWeekday.getDate() + 7
    )

    return (
        nextSameWeekday.getMonth() !==
        date.getMonth()
    )
}


// ============================================================
// NORMALIZE MONTHLY WEEK NUMBERS
// ============================================================

function getWeekNumbers(
    value
) {

    let numbers = value

    if (
        typeof numbers === 'string'
    ) {

        try {

            numbers =
                JSON.parse(
                    numbers
                )

        } catch {

            numbers =
                numbers
                    .split(',')
                    .map(
                        value =>
                            Number(
                                value.trim()
                            )
                    )
        }
    }

    if (
        !Array.isArray(numbers)
    ) {

        return []
    }

    return [
        ...new Set(
            numbers
                .map(Number)
                .filter(
                    number =>
                        Number.isInteger(number) &&
                        (
                            (
                                number >= 1 &&
                                number <= 5
                            ) ||
                            number === -1
                        )
                )
        )
    ].sort(
        (a, b) =>
            a - b
    )
}


// ============================================================
// MONTHLY RECURRENCE EVALUATOR
// ============================================================

export function evaluateMonthly({

    template,

    operationalDate

}) {

    // ========================================================
    // START DATE
    // ========================================================

    const startDate =
        toLocalCalendarDate(
            template.recurrence_start_date
        )


    // ========================================================
    // CURRENT DATE
    // ========================================================

    const currentDate =
        operationalDate instanceof Date

            ? new Date(
                operationalDate.getFullYear(),
                operationalDate.getMonth(),
                operationalDate.getDate()
            )

            : toLocalCalendarDate(
                operationalDate
            )


    // ========================================================
    // SAFETY CHECK
    // ========================================================

    if (!currentDate) {

        return decision({

            generate: false,

            code:
                EngineResult.NOT_READY,

            reason:
                'Monthly recurrence has no valid operational date'

        })
    }


    // ========================================================
    // NOT STARTED
    // ========================================================

    if (
        startDate &&
        currentDate < startDate
    ) {

        return decision({

            generate: false,

            code:
                EngineResult.NOT_STARTED,

            reason:
                'Start date has not been reached'

        })
    }


    // ========================================================
    // END DATE
    // ========================================================

    const endDate =
        toLocalCalendarDate(
            template.recurrence_end_date
        )

    if (
        endDate &&
        currentDate > endDate
    ) {

        return decision({

            generate: false,

            code:
                EngineResult.EXPIRED,

            reason:
                'Template has expired'

        })
    }


    // ========================================================
    // MONTHLY INTERVAL
    // ========================================================
    //
    // NULL = every month.
    //
    // Example:
    //
    // start = January
    // interval = 2
    //
    // January
    // March
    // May
    // July
    //
    // ========================================================

    const interval =
        Math.max(
            1,
            Number(
                template.interval_value
            ) || 1
        )


    // ========================================================
    // CHECK MONTH CADENCE
    // ========================================================

    if (startDate) {

        const monthOffset =
            monthsBetween(
                startDate,
                currentDate
            )

        if (
            monthOffset < 0 ||
            monthOffset % interval !== 0
        ) {

            return decision({

                generate: false,

                code:
                    EngineResult.NOT_READY,

                reason:
                    'Monthly recurrence interval not reached'

            })
        }
    }


    // ========================================================
    // MONTHLY RULE TYPE
    // ========================================================

    const ruleType =
        template.monthly_rule_type


    // ========================================================
    // DAY OF MONTH
    // ========================================================
    //
    // This uses the recurrence start date as the calendar
    // anchor.
    //
    // Example:
    //
    // configured day = 31
    //
    // Jan 31
    // Feb 28
    // Mar 31
    // Apr 30
    // May 31
    //
    // We clamp the requested day to the last day of the
    // current month.
    //
    // ========================================================

    if (
        ruleType === 'day_of_month'
    ) {

        let dayOfMonth =
            Number(
                template.day_of_month
            )


        // ----------------------------------------------------
        // If the configured day is invalid, stop.
        // ----------------------------------------------------

        if (
            !Number.isInteger(
                dayOfMonth
            ) ||
            dayOfMonth < 1 ||
            dayOfMonth > 31
        ) {

            return decision({

                generate: false,

                code:
                    EngineResult.NOT_READY,

                reason:
                    'Monthly day-of-month is invalid'

            })
        }


        // ----------------------------------------------------
        // Determine the actual calendar day for this month.
        // ----------------------------------------------------

        const daysInMonth =
            getDaysInMonth(
                currentDate.getFullYear(),
                currentDate.getMonth()
            )


        const actualDay =
            Math.min(
                dayOfMonth,
                daysInMonth
            )


        // ----------------------------------------------------
        // Does today match the calendar-adjusted day?
        // ----------------------------------------------------

        if (
            currentDate.getDate() !==
            actualDay
        ) {

            return decision({

                generate: false,

                code:
                    EngineResult.NOT_READY,

                reason:
                    'Monthly day-of-month is not today'

            })
        }


        // ----------------------------------------------------
        // Generate occurrence.
        // ----------------------------------------------------

        return decision({

            generate: true,

            code:
                EngineResult.READY,

            reason:
                'Monthly day-of-month recurrence',

            dueDate:
                currentDate

        })
    }


    // ========================================================
    // NTH WEEKDAY
    // ========================================================

    if (
        ruleType === 'nth_weekday'
    ) {

        const requestedWeekday =
            Number(
                template.monthly_weekday
            )


        // ----------------------------------------------------
        // Validate weekday.
        // ----------------------------------------------------

        if (
            !Number.isInteger(
                requestedWeekday
            ) ||
            requestedWeekday < 0 ||
            requestedWeekday > 6
        ) {

            return decision({

                generate: false,

                code:
                    EngineResult.NOT_READY,

                reason:
                    'Monthly weekday is invalid'

            })
        }


        // ----------------------------------------------------
        // Normalize week numbers.
        // ----------------------------------------------------

        const weekNumbers =
            getWeekNumbers(
                template.monthly_week_numbers
            )


        if (
            weekNumbers.length === 0
        ) {

            return decision({

                generate: false,

                code:
                    EngineResult.NOT_READY,

                reason:
                    'Monthly weekday recurrence has no week numbers'

            })
        }


        // ----------------------------------------------------
        // Must be the requested weekday.
        // ----------------------------------------------------

        if (
            currentDate.getDay() !==
            requestedWeekday
        ) {

            return decision({

                generate: false,

                code:
                    EngineResult.NOT_READY,

                reason:
                    'Monthly recurrence weekday is not today'

            })
        }


        // ----------------------------------------------------
        // Determine occurrence number.
        // ----------------------------------------------------

        const occurrenceNumber =
            getWeekdayOccurrence(
                currentDate,
                requestedWeekday
            )


        // ----------------------------------------------------
        // Normal nth occurrence.
        // ----------------------------------------------------

        if (
            occurrenceNumber !== null &&
            weekNumbers.includes(
                occurrenceNumber
            )
        ) {

            return decision({

                generate: true,

                code:
                    EngineResult.READY,

                reason:
                    'Monthly nth-weekday recurrence',

                dueDate:
                    currentDate

            })
        }


        // ----------------------------------------------------
        // Last weekday.
        // ----------------------------------------------------

        if (
            weekNumbers.includes(-1) &&
            isLastWeekday(
                currentDate,
                requestedWeekday
            )
        ) {

            return decision({

                generate: true,

                code:
                    EngineResult.READY,

                reason:
                    'Monthly last-weekday recurrence',

                dueDate:
                    currentDate

            })
        }


        // ----------------------------------------------------
        // Not a scheduled occurrence.
        // ----------------------------------------------------

        return decision({

            generate: false,

            code:
                EngineResult.NOT_READY,

            reason:
                'Monthly weekday occurrence is not scheduled'

        })
    }


    // ========================================================
    // NO VALID MONTHLY RULE
    // ========================================================

    return decision({

        generate: false,

        code:
            EngineResult.NOT_READY,

        reason:
            'Monthly recurrence has no valid rule configured'

    })
}